import React, { useState, useEffect } from "react";
import { AppHeader } from "../components/CommunitySelection";
import { listLessons, createLesson, createLessonComment } from "../lib/api";
import { toast } from "sonner";
import { useLang } from "../lib/i18n";
import { MessageCircle, Send, Lightbulb, CornerDownRight } from "lucide-react";

const fmtDate = (iso, lang) => {
  try {
    return new Date(iso).toLocaleDateString(lang === "es" ? "es-ES" : "en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch (e) {
    return "";
  }
};

const CommentForm = ({ lessonId, onDone }) => {
  const { t } = useLang();
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      await createLessonComment(lessonId, { name: name.trim(), text: text.trim() });
      setText("");
      setName("");
      toast.success(t("Thanks — your reply will appear once reviewed."));
      onDone && onDone();
    } catch (e) {
      toast.error(t("Could not post your reply. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 pl-4 border-l-2 border-vin/15">
      <input
        data-testid="comment-name-input"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={t("First name or initials (optional)")}
        maxLength={40}
        className="w-full rounded-lg border border-[var(--line)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-vin/40 focus:border-vin"
      />
      <textarea
        data-testid="comment-text-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={2}
        placeholder={t("Add to the conversation…")}
        className="mt-2 w-full rounded-lg border border-[var(--line)] p-3 text-sm focus:outline-none focus:ring-2 focus:ring-vin/40 focus:border-vin resize-none"
      />
      <button
        data-testid="submit-comment-button"
        onClick={submit}
        disabled={!text.trim() || busy}
        className="mt-2 inline-flex items-center gap-2 text-vin text-sm font-semibold px-4 py-2 rounded-full border border-vin/30 hover:bg-vin/5 transition-colors duration-200 disabled:opacity-40"
      >
        <CornerDownRight className="w-4 h-4" strokeWidth={1.75} /> {t("Reply")}
      </button>
    </div>
  );
};

const LessonCard = ({ lesson, onRefresh }) => {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="lesson-card" className="bg-white rounded-lg border border-[var(--line)] p-6">
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-display font-semibold text-ink">{lesson.name}</span>
        <span className="text-xs text-ink-soft shrink-0">{fmtDate(lesson.created_at, lang)}</span>
      </div>
      {lesson.community_name && (
        <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-vin/70 mt-1">
          {t("Walked with a neighbor in")} {lesson.community_name}
        </div>
      )}
      <p className="mt-3 text-[15px] text-ink leading-relaxed whitespace-pre-wrap">{lesson.text}</p>

      {lesson.comments.length > 0 && (
        <div className="mt-5 space-y-3">
          {lesson.comments.map((c) => (
            <div key={c.id} className="pl-4 border-l-2 border-[var(--line)]">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-sm font-semibold text-ink">{c.name}</span>
                <span className="text-[11px] text-ink-soft shrink-0">{fmtDate(c.created_at, lang)}</span>
              </div>
              <p className="text-sm text-ink-soft leading-relaxed whitespace-pre-wrap mt-0.5">{c.text}</p>
            </div>
          ))}
        </div>
      )}

      {open ? (
        <CommentForm lessonId={lesson.id} onDone={() => setOpen(false)} />
      ) : (
        <button
          data-testid="reply-toggle-button"
          onClick={() => setOpen(true)}
          className="mt-4 inline-flex items-center gap-2 text-vin text-sm font-semibold hover:underline underline-offset-4"
        >
          <MessageCircle className="w-4 h-4" strokeWidth={1.75} /> {t("Add to the conversation")}
        </button>
      )}
    </div>
  );
};

export default function LessonsWall() {
  const { t } = useLang();
  const [lessons, setLessons] = useState(null);
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const data = await listLessons();
      setLessons(data.lessons);
    } catch (e) {
      setLessons([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      await createLesson({ name: name.trim(), text: text.trim() });
      setText("");
      setName("");
      toast.success(t("Thank you — your lesson will appear on the wall once reviewed."));
    } catch (e) {
      toast.error(t("Could not share your lesson. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--page)]">
      <AppHeader subtitle={t("Lessons Learned")} />
      <div className="max-w-3xl mx-auto px-5 sm:px-6 py-10 sm:py-12">
        <div className="animate-rise">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-vin/70 mb-3">
            {t("A shared conversation")}
          </p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-ink leading-tight">
            {t("Lessons Learned")}
          </h1>
          <p className="mt-4 text-base text-ink-soft leading-relaxed">
            {t("What did walking a year in a neighbor's shoes reveal to you? Share a reflection, and read what others carried away. Together these lessons become a conversation about the root causes of poverty and how we can respond.")}
          </p>
        </div>

        {/* Share form */}
        <div className="mt-8 bg-white rounded-lg border border-vin/20 p-6 sm:p-7">
          <div className="flex items-center gap-2 text-vin mb-4">
            <Lightbulb className="w-4 h-4" strokeWidth={1.75} />
            <span className="text-[11px] uppercase tracking-[0.14em] font-semibold">{t("Share what you learned")}</span>
          </div>
          <input
            data-testid="lesson-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("First name or initials (optional)")}
            maxLength={40}
            className="w-full rounded-lg border border-[var(--line)] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-vin/40 focus:border-vin"
          />
          <textarea
            data-testid="lesson-text-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={4}
            placeholder={t("What did this experience help you understand?")}
            className="mt-3 w-full rounded-lg border border-[var(--line)] p-4 text-sm focus:outline-none focus:ring-2 focus:ring-vin/40 focus:border-vin resize-none"
          />
          <button
            data-testid="share-lesson-button"
            onClick={submit}
            disabled={!text.trim() || busy}
            className="mt-3 inline-flex items-center gap-2 bg-vin text-white font-semibold px-6 py-3 rounded-full hover:bg-vin-hover transition-colors duration-200 disabled:opacity-40"
          >
            <Send className="w-4 h-4" strokeWidth={1.75} />
            {busy ? t("Sharing…") : t("Share Lessons Learned")}
          </button>
          <p className="mt-3 text-xs text-ink-soft">
            {t("Posts are reviewed before appearing on the wall.")}
          </p>
        </div>

        {/* Wall */}
        <div className="mt-10 space-y-5">
          {lessons === null ? (
            <p className="text-ink-soft">{t("Loading…")}</p>
          ) : lessons.length === 0 ? (
            <p data-testid="lessons-empty" className="text-ink-soft text-center py-8">
              {t("No lessons have been shared yet. Be the first to add one.")}
            </p>
          ) : (
            lessons.map((l) => <LessonCard key={l.id} lesson={l} onRefresh={load} />)
          )}
        </div>
      </div>
    </div>
  );
}
